import { queryOptions } from "@tanstack/react-query";
import type { RequestConfig } from "@app/config/axios/http";
import { divisionApi } from "@src/api/dictionaries/divisions";

/** Базовый ключ query доступности назначенных подразделений пользователя. */
export const USER_DIVISION_ACCESS_QUERY_KEY = ["session", "user-divisions", "access"] as const;

/** Период актуальности cached Create-доступа. */
export const USER_DIVISION_ACCESS_STALE_TIME_MS = 5 * 60 * 1000;

/** Актуальные server-side capabilities назначенных подразделений пользователя. */
export interface UserDivisionAccess {
    /** Коды назначенных подразделений, доступных для операций создания. */
    createDivisionCodes: number[];
}

/**
 * Загружает пересечение назначенных подразделений с Create-доступом.
 *
 * @param divisionCodes коды назначенных подразделений пользователя
 * @param config конфигурация HTTP-запроса
 */
export async function loadUserDivisionAccess(
    divisionCodes: readonly number[],
    config?: RequestConfig,
): Promise<UserDivisionAccess> {
    if (divisionCodes.length === 0) {
        return { createDivisionCodes: [] };
    }

    const result = await divisionApi.load(
        {
            flt: {
                Codes: [...divisionCodes],
                AvailableFor: "Create",
            },
            limit: divisionCodes.length,
            offset: 0,
        },
        config,
    );

    return {
        createDivisionCodes: result.items.map((division) => division.Code),
    };
}

/**
 * Создаёт query options проверки Create-доступа для назначенных подразделений.
 *
 * Сервер получает только `DivisionCodes` текущего пользователя, поэтому query
 * возвращает их пересечение с `AvailableFor: "Create"`, а не общий каталог.
 *
 * @param divisionCodes коды назначенных подразделений пользователя
 */
export function userDivisionAccessQueryOptions(divisionCodes: readonly number[]) {
    return queryOptions({
        queryKey: [...USER_DIVISION_ACCESS_QUERY_KEY, divisionCodes],
        queryFn: ({ signal }) => loadUserDivisionAccess(divisionCodes, { signal }),
        staleTime: USER_DIVISION_ACCESS_STALE_TIME_MS,
        meta: {
            errorSource: "session.user-divisions.access",
        },
    });
}
